import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@wix/design-system';
import { Header } from './Header';
import { Title } from './Title';
import { Subtitle } from './Subtitle';
import { InputDialog } from './InputDialog';
import { GetIdeaSection } from './GetIdeaSection';
import { CliTemplatesSection } from './CliTemplatesSection';
import s from './IntroPage.scss';
import {
  useErrorHandler,
  useExperiments,
  useHttpClient,
  useTranslation,
} from '@wix/fe-essentials-standalone/react';
import { useAppContext } from '../App/AppContext';
import { FullScreenLoader } from '../FullScreenLoader/FullScreenLoader';
import { useOnboardingPageBi } from '../../hooks/useOnboardingPageBi';
import { CliTemplate } from './CliTemplatesSection/types';
import { queryClient } from '../../utils/queryClient';
import {
  buildAppBuilderUrl,
  getReferrerFromCurrentUrl,
} from '@wix/dev-center-pages-common/deVibe';
import { createAppBuilderApplication } from '@wix/ambassador-devcenter-devibe-server-v1-project/http';

const DEVISE_ONBOARDING_PROMPT_KEY = 'devibe_onboarding_prompt';

const initialPromptFromSession =
  sessionStorage.getItem(DEVISE_ONBOARDING_PROMPT_KEY) || '';
sessionStorage.removeItem(DEVISE_ONBOARDING_PROMPT_KEY);

export function IntroPage() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(initialPromptFromSession);
  const { reportPageLoadEnd, reportBiClick } = useOnboardingPageBi();
  const hasPreviousPromptRef = useRef(!!initialPromptFromSession);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [generateAppStatus, setGenerateAppStatus] = useState<
    'GENERATING_WITH_PROMPT' | 'GENERATING_WITHOUT_PROMPT' | undefined
  >(prompt ? 'GENERATING_WITH_PROMPT' : undefined);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const httpClient = useHttpClient();
  const errorHandler = useErrorHandler();
  const { userId } = useAppContext();
  const { experiments } = useExperiments({ readOnly: true });
  const skipBlueprintApproval = experiments.enabled(
    'specs.dcc.devibe.skipBlueprintApproval',
  );

  useEffect(() => {
    reportPageLoadEnd();
  }, []);
  const handlePromptChange = (value: string) => {
    setPrompt(value);
  };

  useEffect(() => {
    if (hasPreviousPromptRef.current && userId) {
      handleSend();
      return;
    }

    textAreaRef.current?.focus();
  }, []);

  const createDeVibeApp = async (
    templateId?: string | null,
    appName?: string,
  ) => {
    const {
      data: { appId },
    } = await queryClient.fetchQuery({
      retry: 5,
      queryKey: [
        'create-app-builder-application',
        templateId ?? null,
        appName ?? null,
      ],
      queryFn: () =>
        errorHandler.withErrorHandler(
          () =>
            httpClient.request(
              createAppBuilderApplication({
                templateId: templateId ?? undefined,
                appName,
              }),
            ),
          {
            errorCodesMap: {},
          },
        ),
    });

    if (!appId) {
      throw new Error('App was not created');
    }

    // Need to wait for Kafka events to fire from DCS after creating an app
    await new Promise((resolve) => setTimeout(resolve, 3000));

    return appId;
  };

  const handleSend = async ({ skipPrompt } = { skipPrompt: false }) => {
    reportBiClick({
      buttonName: 'Start Building',
      promptText: skipPrompt ? '' : prompt,
    });
    if (generateAppStatus) {
      return;
    }

    if (!skipPrompt && !prompt.trim()) {
      setErrorMessage(t('devibe.onboarding.input_empty_error'));
      return;
    }

    setGenerateAppStatus(
      skipPrompt ? 'GENERATING_WITHOUT_PROMPT' : 'GENERATING_WITH_PROMPT',
    );

    if (!userId) {
      sessionStorage.setItem(DEVISE_ONBOARDING_PROMPT_KEY, prompt);
      const loginUrl = new URL('https://users.wix.com/signin/login');
      loginUrl.searchParams.set('postLogin', window.location.href);
      loginUrl.searchParams.set('postSignUp', window.location.href);
      loginUrl.searchParams.set('studio', 'true');
      window.location.href = loginUrl.toString();
      return;
    }

    try {
      const appId = await createDeVibeApp();

      if (skipPrompt) {
        window.location.assign(
          buildAppBuilderUrl({
            appId,
            tab: 'ide',
            initialPrompt: '',
            referrer: getReferrerFromCurrentUrl(),
          }),
        );
      } else {
        window.location.assign(
          buildAppBuilderUrl({
            appId,
            tab: skipBlueprintApproval ? 'ide' : 'app-builder',
            initialPrompt: prompt,
            referrer: getReferrerFromCurrentUrl(),
          }),
        );
      }
    } catch (error) {
      console.error('Failed to create DeVibe app:', error);
      setErrorMessage(errorHandler.getResolvedError(error).message);
      setGenerateAppStatus(undefined);
    }
  };

  const handleTemplateClick = async (template: CliTemplate) => {
    if (generateAppStatus) {
      return;
    }

    setGenerateAppStatus('GENERATING_WITHOUT_PROMPT');

    if (!userId) {
      const loginUrl = new URL('https://users.wix.com/signin/login');
      loginUrl.searchParams.set('postLogin', window.location.href);
      loginUrl.searchParams.set('postSignUp', window.location.href);
      window.location.href = loginUrl.toString();
      return;
    }

    try {
      const appId = await createDeVibeApp(template.id, template.title);

      window.location.assign(
        buildAppBuilderUrl({
          appId,
          tab: 'ide',
          initialPrompt: '',
          templateId: template.id,
          referrer: getReferrerFromCurrentUrl(),
        }),
      );
    } catch (error) {
      console.error('Failed to create DeVibe app:', error);
      setErrorMessage(errorHandler.getResolvedError(error).message);
      setGenerateAppStatus(undefined);
    }
  };

  const handleSuggestionSelect = (suggestion: string) => {
    setPrompt(suggestion);
    textAreaRef.current?.focus();
  };

  return (
    <Box
      direction="vertical"
      width="100%"
      minHeight="100vh"
      dataHook="devibe-onboarding-page"
    >
      <Box flex={1} direction="vertical" width="100%" className={s.root}>
        <Header />

        <Box direction="vertical" verticalAlign="middle" flex={1}>
          <Box
            width="100%"
            direction="vertical"
            align="center"
            gap="16px"
            className={s.content}
          >
            <Box direction="vertical" gap="SP1">
              <Box maxWidth="100%" align="center" textAlign="center">
                <Title />
              </Box>
              <Box align="center" textAlign="center">
                <Subtitle />
              </Box>
            </Box>
            <Box
              maxWidth="686px"
              direction="vertical"
              className={s.inputSection}
            >
              <InputDialog
                prompt={prompt}
                onPromptChange={handlePromptChange}
                onSend={handleSend}
                handleSuggestionSelect={handleSuggestionSelect}
                isLoading={generateAppStatus === 'GENERATING_WITH_PROMPT'}
                textAreaRef={textAreaRef}
              />
              {errorMessage && (
                <Box
                  direction="vertical"
                  width="100%"
                  align="center"
                  marginTop="SP3"
                >
                  <Text size="small" secondary skin="error">
                    {errorMessage}
                  </Text>
                </Box>
              )}
              <Box
                direction="vertical"
                width="100%"
                align="center"
                marginTop="SP3"
                textAlign="center"
              >
                <Text size="small" secondary>
                  {t('devibe.onboarding.ai_mistakes_disclaimer')}
                </Text>
              </Box>
            </Box>
            <Box width="100%" className={s.templatesSection}>
              <CliTemplatesSection
                onTemplateClick={handleTemplateClick}
                onStartWithoutPrompt={() => handleSend({ skipPrompt: true })}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <GetIdeaSection />

      {generateAppStatus === 'GENERATING_WITHOUT_PROMPT' && (
        <FullScreenLoader
          label={t('devibe.onboarding.creating_app_loader_label')}
        />
      )}
    </Box>
  );
}
